import type { TrendMarker } from '@/api/revenue'
import { formatInteger, formatValue, NARROW_QUERY, useMediaQuery, type FormatOptions, type ValueFormat } from '@/design'
import { ChartDataTable, useChartCursor } from '@/design/chartParts'
import { niceMax } from '@/design/TrendChart'

import { ChartReadout } from '../ChartReadout'

/** One bucket as the chart draws it: the plotted figure, its comparison, and what the readout adds. */
export interface TrendChartBucket {
  key: string
  /** The x-axis label: "3 Sep", "14:00", "Sep 2026". */
  label: string
  /** The readout's fuller name for the bucket: "Thu 3 Sep", "3 – 9 Sep". */
  title: string
  value: number
  /** The aligned comparison bucket's figure; null where there is none. */
  previous: number | null
  revenue: number
  orders: number
  tickets: number
  markers: readonly TrendMarker[]
}

/** The viewBox's width: narrower on a phone, so the axis text stays legible when the chart is scaled down. */
const WIDE = 640
const NARROW = 360
const PAD = { left: 60, right: 12, top: 18, bottom: 28 }
const Y_TICKS = 4
const MAX_X_LABELS = 7

export const MARKER_LABELS = { launch: 'Competition launch', draw: 'Draw' } as const

function domain(values: readonly number[]): { low: number; high: number } {
  const max = Math.max(0, ...values)
  const min = Math.min(0, ...values)
  return { low: min < 0 ? -niceMax(-min) : 0, high: max > 0 ? niceMax(max) : min < 0 ? 0 : 1 }
}

/** A launch is a triangle, a draw a diamond: the marker's shape says which, its colour repeats it. */
function MarkerGlyph({ type, x, y }: { type: TrendMarker['type']; x: number; y: number }) {
  return type === 'launch' ? (
    <path d={`M${x - 5},${y + 4} L${x},${y - 5} L${x + 5},${y + 4} Z`} className="rv-marker__glyph" data-type="launch" />
  ) : (
    <path d={`M${x},${y - 5} L${x + 5},${y} L${x},${y + 5} L${x - 5},${y} Z`} className="rv-marker__glyph" data-type="draw" />
  )
}

/** The legend's swatch for a marker: the same glyph, drawn small. */
export function MarkerKey({ type }: { type: TrendMarker['type'] }) {
  return (
    <span className="rv-legend__item" data-series={type}>
      <svg width="12" height="12" viewBox="-6 -6 12 12" aria-hidden="true" focusable="false">
        <MarkerGlyph type={type} x={0} y={0} />
      </svg>
      {MARKER_LABELS[type]}
    </span>
  )
}

function markerText(markers: readonly TrendMarker[]): string {
  return markers.map((marker) => `${marker.type === 'launch' ? 'Launch' : 'Draw'}: ${marker.name}`).join('; ')
}

/**
 * The revenue page's trend: one series on one axis (revenue under the basis, or orders),
 * the comparison dashed beneath it when there is one to draw, and each competition launch
 * and draw marked on the bucket it falls in. Pointer hover and the arrow keys move a
 * readout with the bucket's revenue, orders, tickets, comparison and markers; the whole
 * series is a visually hidden table too.
 */
export function RevenueTrendChart({
  title,
  buckets,
  format,
  money,
  seriesLabel,
  previousLabel,
}: {
  title: string
  buckets: readonly TrendChartBucket[]
  format: ValueFormat
  money: FormatOptions
  seriesLabel: string
  /** Null draws no comparison line (none to draw, or no room for it on a phone). */
  previousLabel: string | null
}) {
  const viewWidth = useMediaQuery(NARROW_QUERY) ? NARROW : WIDE
  const height = 240
  const innerWidth = viewWidth - PAD.left - PAD.right
  const innerHeight = height - PAD.top - PAD.bottom
  const cursor = useChartCursor(buckets.length, viewWidth, (position) =>
    Math.round(((position - PAD.left) / innerWidth) * Math.max(1, buckets.length - 1)),
  )
  const { active, readoutId } = cursor
  if (buckets.length === 0) return null

  const overlay = previousLabel !== null
  const values = buckets.flatMap((bucket) => (overlay && bucket.previous !== null ? [bucket.value, bucket.previous] : [bucket.value]))
  const { low, high } = domain(values)
  const x = (index: number) => PAD.left + (buckets.length === 1 ? innerWidth / 2 : (index / (buckets.length - 1)) * innerWidth)
  const y = (value: number) => PAD.top + innerHeight - ((value - low) / (high - low)) * innerHeight
  const fmt = (value: number, compact = false) => formatValue(value, format, { ...money, compact })
  const revenue = (value: number) => formatValue(value, 'currency', money)

  const line = (pick: (bucket: TrendChartBucket) => number | null) => {
    let path = ''
    let pen = false
    buckets.forEach((bucket, index) => {
      const value = pick(bucket)
      if (value === null) {
        pen = false
        return
      }
      path += `${pen ? 'L' : 'M'}${x(index).toFixed(1)},${y(value).toFixed(1)} `
      pen = true
    })
    return path.trim()
  }
  const currentPath = line((bucket) => bucket.value)
  const previousPath = overlay ? line((bucket) => bucket.previous) : ''
  const labelEvery = Math.max(1, Math.ceil(buckets.length / MAX_X_LABELS))
  const activeBucket = active === null ? null : buckets[active]
  // The axis can run below 0 (net of refunds), which the design system's grid (0 up) cannot draw.
  const ticks = Array.from({ length: Y_TICKS + 1 }, (_, tick) => low + ((high - low) / Y_TICKS) * tick)

  return (
    <figure className="ci-trend rv-trend">
      <div className="ci-trend__plot" aria-label={`${title}. Use the arrow keys to read values.`} {...cursor.plotProps}>
        <svg viewBox={`0 0 ${viewWidth} ${height}`} className="ci-trend__svg" aria-hidden="true" focusable="false" {...cursor.svgProps}>
          {ticks.map((value) => (
            <g key={value} className="ci-trend__grid">
              <line x1={PAD.left} x2={viewWidth - PAD.right} y1={y(value)} y2={y(value)} />
              <text x={PAD.left - 8} y={y(value) + 4} textAnchor="end">
                {fmt(value, true)}
              </text>
            </g>
          ))}
          {buckets.map((bucket, index) =>
            index % labelEvery === 0 || index === buckets.length - 1 ? (
              <text key={bucket.key} className="ci-trend__x" x={x(index)} y={height - 8} textAnchor="middle">
                {bucket.label}
              </text>
            ) : null,
          )}
          {buckets.map((bucket, index) =>
            bucket.markers.length > 0 ? (
              <g key={`m-${bucket.key}`} className="rv-marker" data-testid="trend-marker" data-types={[...new Set(bucket.markers.map((marker) => marker.type))].join(' ')}>
                <line x1={x(index)} x2={x(index)} y1={PAD.top} y2={PAD.top + innerHeight} data-type={bucket.markers[0]?.type} />
                {[...new Set(bucket.markers.map((marker) => marker.type))].map((type, at) => (
                  <MarkerGlyph key={type} type={type} x={x(index)} y={PAD.top - 8 + at * 11} />
                ))}
              </g>
            ) : null,
          )}
          {previousPath ? <path d={previousPath} className="ci-trend__previous" data-testid="trend-previous" /> : null}
          <path d={currentPath} className="ci-trend__current" />
          {activeBucket && active !== null ? (
            <g className="ci-trend__cursor">
              <line x1={x(active)} x2={x(active)} y1={PAD.top} y2={PAD.top + innerHeight} />
              <circle cx={x(active)} cy={y(activeBucket.value)} r={4} />
            </g>
          ) : null}
        </svg>
        <ChartReadout id={readoutId} left={active === null ? null : (x(active) / viewWidth) * 100}>
          {activeBucket ? (
            <>
              <strong>{activeBucket.title}</strong>
              <span>Revenue: {revenue(activeBucket.revenue)}</span>
              <span>Orders: {formatInteger(activeBucket.orders, money)}</span>
              <span>Tickets: {formatInteger(activeBucket.tickets, money)}</span>
              {previousLabel ? (
                <span>
                  {previousLabel}: {activeBucket.previous === null ? '—' : fmt(activeBucket.previous)}
                </span>
              ) : null}
              {activeBucket.markers.length > 0 ? <span>{markerText(activeBucket.markers)}</span> : null}
            </>
          ) : null}
        </ChartReadout>
      </div>
      <figcaption className="ci-trend__legend rv-legend">
        <span className="ci-trend__key" data-series="current">
          {seriesLabel}
        </span>
        {previousLabel ? (
          <span className="ci-trend__key" data-series="previous">
            {previousLabel}
          </span>
        ) : null}
        <MarkerKey type="launch" />
        <MarkerKey type="draw" />
      </figcaption>
      <ChartDataTable
        title={title}
        columns={[
          'Period',
          seriesLabel,
          ...(previousLabel ? [previousLabel] : []),
          ...(format === 'currency' ? ['Orders'] : ['Revenue']),
          'Tickets',
          'Launches and draws',
        ]}
        rows={buckets.map((bucket) => ({
          key: bucket.key,
          name: bucket.title,
          cells: [
            fmt(bucket.value),
            ...(previousLabel ? [bucket.previous === null ? '—' : fmt(bucket.previous)] : []),
            format === 'currency' ? formatInteger(bucket.orders, money) : revenue(bucket.revenue),
            formatInteger(bucket.tickets, money),
            bucket.markers.length > 0 ? markerText(bucket.markers) : '—',
          ],
        }))}
      />
    </figure>
  )
}
